import React, { useMemo } from 'react';

import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import { useCppPartners } from '~/hooks/apis/costPerPoint';

import type { CppPartner } from '~/types/costPerPoint';

/**
 * Ordered list of the six per-partner permission flags and the compact chip
 * label each one renders as. Order here controls the left-to-right chip order.
 */
const PERMISSION_FLAGS: Array<{ field: keyof CppPartner; label: string }> = [
  { field: 'allowCreditPoints', label: 'Credit Pts' },
  { field: 'allowCreditStatusCredits', label: 'Credit SC' },
  { field: 'allowDebitPoints', label: 'Debit Pts' },
  { field: 'allowDebitStatusCredits', label: 'Debit SC' },
  { field: 'allowRefund', label: 'Refund' },
  { field: 'allowTierUpgrade', label: 'Tier Upgrade' },
];

/**
 * Renders the enabled-only permission chips for a partner row.
 * Disabled (false) flags are omitted entirely rather than shown as disabled chips;
 * when no flags are enabled a muted em-dash placeholder is shown instead.
 */
function PermissionsCell({ partner }: { partner: CppPartner }) {
  const enabled = PERMISSION_FLAGS.filter(({ field }) => partner[field] === true);

  if (enabled.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {enabled.map(({ field, label }) => (
        <Chip key={field} size="small" variant="outlined" label={label} />
      ))}
    </Stack>
  );
}

const columns: Array<GridColDef<CppPartner>> = [
  { field: 'name', headerName: 'Name', width: 220 },
  { field: 'partnerType', headerName: 'Partner Type', width: 130 },
  { field: 'status', headerName: 'Status', width: 120 },
  {
    field: 'minPointsPerTransaction',
    headerName: 'Points / Txn',
    width: 140,
    valueGetter: (_value, row) => row.minPointsPerTransaction,
    renderCell: ({ row }) => `${row.minPointsPerTransaction} – ${row.maxPointsPerTransaction}`,
  },
  {
    field: 'minStatusCreditsPerTransaction',
    headerName: 'Status Credits / Txn',
    width: 160,
    valueGetter: (_value, row) => row.minStatusCreditsPerTransaction,
    renderCell: ({ row }) => `${row.minStatusCreditsPerTransaction} – ${row.maxStatusCreditsPerTransaction}`,
  },
  {
    field: 'permissions',
    headerName: 'Permissions',
    width: 280,
    sortable: false,
    renderCell: ({ row }) => <PermissionsCell partner={row} />,
  },
  {
    field: 'requireNameMatch',
    headerName: 'Name Match',
    width: 110,
    valueGetter: (_value, row) => row.requireNameMatch,
    renderCell: ({ row }) => (row.requireNameMatch ? 'Yes' : '—'),
  },
  {
    field: 'failFast',
    headerName: 'Fail Fast',
    width: 100,
    valueGetter: (_value, row) => row.failFast,
    renderCell: ({ row }) => (row.failFast ? 'Yes' : '—'),
  },
];

/**
 * Read-only Société Point partner directory.
 *
 * Lists every partner registered against the CPP rate-card system (name, type, status,
 * enforced per-transaction points/status-credit ranges, and enabled permission flags)
 * with no create/edit/delete actions — partner management happens elsewhere; this is
 * purely a reference view for admins cross-checking rate cards against their owning partner.
 */
export default function PartnersListPage() {
  const { data, isLoading, isError } = useCppPartners();
  const partners: Array<CppPartner> = useMemo(() => data?.result ?? [], [data]);

  return (
    <Box sx={{ p: 1 }}>
      <Stack sx={{ my: 2 }}>
        <Typography variant="h4">Société Point Partners</Typography>
        <Typography variant="body2" color="text.secondary">
          Reference list of network partners and their enforced per-transaction limits and permissions.
        </Typography>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load partners. Please try again.
        </Alert>
      )}

      <Box sx={{ height: 600 }}>
        <DataGridPro
          rows={partners}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
        />
      </Box>
    </Box>
  );
}
